import { SportName } from '@shared/enums/SportName';
import { TagName } from '@shared/enums/TagName';

/**
 * Service for handling HighlightIQ formulas and calculations
 */

// Formula configurations for different sports
const SPORT_FORMULAS = {
	[SportName.HANDBALL]: {
		match_highlights: {
			formula: (tag) => {
				if (!tag.iqScore) return 0;
				const { audioScore, eventScore, timingScore, scoringScore } = tag.iqScore;
				return 0.3 * audioScore + 0.4 * eventScore + 0.3 * ((timingScore * scoringScore) / 10);
			},
			alternateTeams:        true,
			includeExtraTags:      [],
			defaultClipCount:      10,
			defaultTargetDuration: 150,
			useGameTime:           true,
			periodMarkers:         [
				{
					start:          TagName.TIMING_HALFTIME_START,
					end:            TagName.TIMING_HALFTIME_END,
					includeMarkers: {
						start: 'first',
						end:   'last',
					},
				},
			],
		},
		condensed_game: {
			formula: (tag) => {
				if (!tag.iqScore) return 0;
				const { audioScore, eventScore, scoringScore } = tag.iqScore;
				return 0.3 * audioScore + 0.6 * eventScore + 0.1 * scoringScore;
			},
			alternateTeams:        true,
			includeExtraTags:      [],
			defaultClipCount:      32,
			defaultTargetDuration: 480, // 8 minutes in seconds
			useGameTime:           true,
			blocksPerPeriod:       3,
			periodMarkers:         [
				{
					start:          TagName.TIMING_HALFTIME_START,
					end:            TagName.TIMING_HALFTIME_END,
					includeMarkers: {
						start: 'first',
						end:   'last',
					},
				},
			],
			fallbackBlockLength: 10 * 60, // 10 minutes in seconds
		},
		high_emotion: {
			formula: (tag) => {
				if (!tag.iqScore) return 0;
				const { audioScore, eventScore } = tag.iqScore;
				return 0.5 * audioScore + 0.5 * eventScore;
			},
			alternateTeams:        false,
			includeExtraTags:      [],
			defaultClipCount:      10,
			defaultTargetDuration: 150,
		},
		tense_moments: {
			formula: (tag) => {
				if (!tag.iqScore) return 0;
				const { audioScore, eventScore, timingScore, scoringScore } = tag.iqScore;
				return 0.2 * audioScore + 0.3 * eventScore + 0.5 * ((timingScore * scoringScore) / 10);
			},
			alternateTeams:        false,
			includeExtraTags:      [],
			defaultClipCount:      10,
			defaultTargetDuration: 150,
		},
	},
};

/**
 * Check if a sport has HighlightIQ formulas
 * @param {String} sportName
 * @returns {Boolean}
 */
export const hasSportFormulas = (sportName) => {
	return !!SPORT_FORMULAS[sportName];
};

/**
 * Get the formulas for a given sport
 * @param {String} sportName
 * @returns {Object|null}
 */
export const getSportFormulas = (sportName) => {
	return SPORT_FORMULAS[sportName] || null;
};

/**
 * Get the default clip count for a formula
 * @param {String} sportName
 * @param {String|Object} formulaType
 * @returns {Number}
 */
export const getDefaultClipCount = (sportName, formulaType) => {
	const formulas = getSportFormulas(sportName);
	// Use formulaType.value if it's an object, otherwise use formulaType directly
	const formulaKey = typeof formulaType === 'object' && formulaType.value ? formulaType.value : formulaType;
	return formulas?.[formulaKey]?.defaultClipCount || 10;
};

/**
 * Get the default target duration for a formula (in seconds)
 * @param {String} sportName
 * @param {String|Object} formulaType
 * @returns {Number}
 */
export const getDefaultTargetDuration = (sportName, formulaType) => {
	const formulas = getSportFormulas(sportName);
	// Use formulaType.value if it's an object, otherwise use formulaType directly
	const formulaKey = typeof formulaType === 'object' && formulaType.value ? formulaType.value : formulaType;
	return formulas?.[formulaKey]?.defaultTargetDuration || 150;
};

/**
 * Check if teams have adequate distribution for team-based formulas
 * @param {Array} tags
 * @returns {Boolean}
 */
export const hasTeamDistribution = (tags) => {
	const teamIds = new Set(tags.map(tag => tag.teamId).filter(Boolean));
	return teamIds.size >= 2;
};

/**
 * Calculate scores for tags based on the formula
 * @param {Array} tags - Tags to calculate scores for
 * @param {String} sportName - Name of the sport
 * @param {String} formulaType - Type of formula to use
 * @returns {Array} Tags with calculated scores
 */
export const calculateTagScores = (tags, sportName, formulaType, customFormulaConfig = null) => {
	const formulas = getSportFormulas(sportName);
	// Use formulaType.value if it's an object, otherwise use formulaType directly
	const formulaKey = typeof formulaType === 'object' && formulaType.value ? formulaType.value : formulaType;

	// If a customFormulaConfig is provided, use it directly. Otherwise, look up from SPORT_FORMULAS.
	const formulaConfigToUse = customFormulaConfig || (formulas ? formulas[formulaKey] : null);

	if (!formulaConfigToUse || !formulaConfigToUse.formula) return tags.map(tag => ({ ...tag, calculatedScore: 0 })); // Return tags with 0 score if no formula

	// const formulaConfig = formulas[formulaKey];

	return tags.map(tag => ({
		...tag,
		calculatedScore: formulaConfigToUse.formula(tag),
	}));
};

/**
 * Helper function to get the appropriate period markers based on configuration
 * @param {Array} tags - All available tags
 * @param {Object} periodConfig - Period configuration
 * @returns {Object} Start and end markers
 */
const getPeriodMarkers = (tags, periodConfig) => {
	// Sort all tags by timestamp first to ensure correct order
	const sortedTags = [...tags].sort((a, b) => a.timestamp - b.timestamp);

	// Find all start and end markers in chronological order
	const startMarkers = sortedTags.filter(tag => tag.tag?.name === periodConfig.start);
	const endMarkers = sortedTags.filter(tag => tag.tag?.name === periodConfig.end);

	if (!startMarkers.length || !endMarkers.length) {
		return { start: null, end: null };
	}

	// Get the appropriate markers based on configuration
	let startMarker = null;
	let endMarker = null;

	// For start markers, we want the first one
	if (periodConfig.includeMarkers?.start === 'last') {
		startMarker = startMarkers[startMarkers.length - 1];
	} else {
		startMarker = startMarkers[0];
	}

	// For end markers, we want the last one
	if (periodConfig.includeMarkers?.end === 'first') {
		endMarker = endMarkers[0];
	} else {
		endMarker = endMarkers[endMarkers.length - 1];
	}

	// Validate that the markers are in the correct order
	if (startMarker && endMarker && startMarker.timestamp > endMarker.timestamp) {
		// If markers are in wrong order, try to find the correct pair
		const validStartMarkers = startMarkers.filter(start =>
			endMarkers.some(end => end.timestamp > start.timestamp)
		);
		const validEndMarkers = endMarkers.filter(end =>
			startMarkers.some(start => start.timestamp < end.timestamp)
		);

		if (validStartMarkers.length && validEndMarkers.length) {
			startMarker = periodConfig.includeMarkers?.start === 'last'
				? validStartMarkers[validStartMarkers.length - 1]
				: validStartMarkers[0];

			endMarker = periodConfig.includeMarkers?.end === 'first'
				? validEndMarkers[0]
				: validEndMarkers[validEndMarkers.length - 1];
		} else {
			// If no valid pairs found, return null
			return { start: null, end: null };
		}
	}

	return { start: startMarker, end: endMarker };
};

/**
 * Select tags based on the formula configuration
 * @param {Array} tags - All available tags
 * @param {Object} event - Event data
 * @param {String} formulaType - Type of formula to use
 * @param {String} limitType - 'clips' or 'seconds'
 * @param {Number} limitValue - Number of clips or seconds
 * @returns {Array} Selected tags
 */
export const selectTagsWithFormula = (tags, event, formulaType, limitType, limitValue, customFormulaConfig = null) => {
	const sportName = event?.sport?.name;

	if (!sportName || (!hasSportFormulas(sportName) && !customFormulaConfig)) {
		return [];
	}

	const formulas = getSportFormulas(sportName);
	const formulaKey = typeof formulaType === 'object' && formulaType.value ? formulaType.value : formulaType;

	// Use customFormulaConfig if provided, otherwise lookup from SPORT_FORMULAS
	const formulaConfig = customFormulaConfig || (formulas ? formulas[formulaKey] : null);

	if (!formulaConfig) {
		return [];
	}

	// If limit value is 0, return empty selection without showing any error
	if (limitValue === 0) {
		return [];
	}

	// Filter out tags without iqScore
	const tagsWithScores = tags.filter(tag => tag.iqScore);

	// Calculate scores for all tags using the effective formula config (either standard or custom)
	const scoredTags = calculateTagScores(tagsWithScores, sportName, formulaKey, formulaConfig);

	// Collect extra tags if formula config requires it
	const extraTags = [];
	if (formulaConfig.includeExtraTags?.length) {
		formulaConfig.includeExtraTags.forEach(tagName => {
			const matchingTags = tags.filter(tag => tag.tag?.name === tagName);
			if (matchingTags.length) {
				extraTags.push(...matchingTags);
			}
		});
	}

	// Handle period markers if configured
	if (formulaConfig.useGameTime && formulaConfig.periodMarkers?.length) {
		formulaConfig.periodMarkers.forEach(periodConfig => {
			const { start, end } = getPeriodMarkers(tags, periodConfig);
			if (start) extraTags.push(start);
			if (end) extraTags.push(end);
		});
	}

	// Sort extraTags by timestamp to prioritize them in sequential order
	extraTags.sort((a, b) => a.timestamp - b.timestamp);

	// Handle tags by team if needed
	let selectedTags = [];
	let remainingLimit = limitValue;

	// First add extra tags counting towards the limit
	if (extraTags.length > 0) {
		if (limitType === 'clips') {
			// For clip limit, take as many extra tags as will fit
			const extraTagsToInclude = extraTags.slice(0, remainingLimit);
			selectedTags = [...extraTagsToInclude];
			remainingLimit -= extraTagsToInclude.length;
		} else {
			// For time limit, take extra tags until we reach the limit
			let totalDuration = 0;

			for (const tag of extraTags) {
				const duration = tag.secondsAfter + tag.secondsBefore;
				if (totalDuration + duration > remainingLimit) {
					break;
				}

				selectedTags.push(tag);
				totalDuration += duration;
			}

			remainingLimit -= totalDuration;
		}
	}

	// If we still have remaining limit, select more tags
	if (remainingLimit > 0) {
		let additionalTags = [];

		if (formulaConfig.alternateTeams === true && hasTeamDistribution(scoredTags)) {
			additionalTags = selectAlternatingTeamTags(scoredTags, formulaConfig, limitType, remainingLimit);
		} else if (formulaKey === 'condensed' && formulaConfig.useGameTime) {
			// For condensed, select from time sections
			additionalTags = selectTagsFromTimeSections(
				scoredTags,
				event.durationSeconds,
				limitType,
				remainingLimit,
				formulaConfig
			);
		} else {
			// Sort by score descending
			const sortedTags = [...scoredTags].sort((a, b) => b.calculatedScore - a.calculatedScore);

			// Select based on limit type
			if (limitType === 'clips') {
				additionalTags = sortedTags.slice(0, remainingLimit);
			} else {
				// Select tags until we reach the time limit
				let totalDuration = 0;

				for (const tag of sortedTags) {
					const duration = tag.secondsAfter + tag.secondsBefore;
					if (totalDuration + duration > remainingLimit) {
						break;
					}

					additionalTags.push(tag);
					totalDuration += duration;
				}
			}
		}

		selectedTags = [...selectedTags, ...additionalTags];
	}

	// Sort by timestamp
	return selectedTags.sort((a, b) => a.timestamp - b.timestamp);
};

/**
 * Select tags alternating between teams
 * @param {Array} scoredTags
 * @param {Object} formulaConfig
 * @param {String} limitType
 * @param {Number} limitValue
 * @returns {Array}
 */
const selectAlternatingTeamTags = (scoredTags, formulaConfig, limitType, limitValue) => {
	// Group tags by team
	const teamTags = {};

	for (const tag of scoredTags) {
		if (!tag.teamId) continue; // Skip tags without teamId

		if (!teamTags[tag.teamId]) {
			teamTags[tag.teamId] = [];
		}

		teamTags[tag.teamId].push(tag);
	}

	// Sort each team's tags by score
	const teams = Object.keys(teamTags);
	teams.forEach(teamId => {
		teamTags[teamId].sort((a, b) => b.calculatedScore - a.calculatedScore);
	});

	// No teams with tags found - return empty array
	if (teams.length === 0) {
		return [];
	}

	// If only one team has tags, just take the top scoring tags from that team
	if (teams.length === 1) {
		const teamId = teams[0];
		if (limitType === 'clips') {
			return teamTags[teamId].slice(0, limitValue);
		} else {
			// Duration-based selection
			let totalDuration = 0;
			const selected = [];

			for (const tag of teamTags[teamId]) {
				const duration = tag.secondsAfter + tag.secondsBefore;
				if (totalDuration + duration > limitValue) {
					break;
				}
				selected.push(tag);
				totalDuration += duration;
			}

			return selected;
		}
	}

	// We have at least two teams
	const selectedTags = [];

	if (limitType === 'clips') {
		// Calculate clips per team - divide evenly between teams
		const clipsPerTeam = Math.floor(limitValue / teams.length);
		const remainingClips = limitValue - (clipsPerTeam * teams.length);

		// Determine which team gets the extra clip if count is odd
		let extraClipTeam = null;
		if (remainingClips > 0) {
			// Find team with next highest score
			let highestScore = -1;

			teams.forEach(teamId => {
				// Skip teams that don't have enough tags to fill their quota
				if (teamTags[teamId].length <= clipsPerTeam) return;

				const nextTagScore = teamTags[teamId][clipsPerTeam].calculatedScore;
				if (nextTagScore > highestScore) {
					highestScore = nextTagScore;
					extraClipTeam = teamId;
				}
			});
		}

		// Select tags for each team
		teams.forEach(teamId => {
			const teamQuota = teamId === extraClipTeam ? clipsPerTeam + 1 : clipsPerTeam;
			const tagsToTake = Math.min(teamQuota, teamTags[teamId].length);
			selectedTags.push(...teamTags[teamId].slice(0, tagsToTake));
		});
	} else {
		// Duration-based selection
		// First calculate the total available duration per team
		const teamDurations = {};
		let totalAvailableDuration = 0;

		teams.forEach(teamId => {
			let teamDuration = 0;
			teamTags[teamId].forEach(tag => {
				teamDuration += (tag.secondsAfter + tag.secondsBefore);
			});
			teamDurations[teamId] = teamDuration;
			totalAvailableDuration += teamDuration;
		});

		// If we have less total duration than requested, use all available tags
		if (totalAvailableDuration <= limitValue) {
			teams.forEach(teamId => {
				selectedTags.push(...teamTags[teamId]);
			});
		} else {
			// Divide duration target evenly between teams
			const targetDurationPerTeam = limitValue / teams.length;

			// For each team, select tags up to their duration quota
			teams.forEach(teamId => {
				let teamUsedDuration = 0;
				const availableDuration = Math.min(targetDurationPerTeam, teamDurations[teamId]);

				for (const tag of teamTags[teamId]) {
					const duration = tag.secondsAfter + tag.secondsBefore;
					if (teamUsedDuration + duration > availableDuration) {
						break;
					}
					selectedTags.push(tag);
					teamUsedDuration += duration;
				}
			});

			// If we still have remaining duration and some teams couldn't fill their quota,
			// distribute it to teams that have more available tags
			const usedDuration = selectedTags.reduce((total, tag) => total + (tag.secondsAfter + tag.secondsBefore), 0);
			const remainingDuration = limitValue - usedDuration;

			if (remainingDuration > 0) {
				// Find teams with remaining tags
				const teamsWithRemainingTags = teams.filter(teamId => {
					const selectedTeamTags = selectedTags.filter(tag => tag.teamId === teamId);
					return selectedTeamTags.length < teamTags[teamId].length;
				});

				if (teamsWithRemainingTags.length > 0) {
					// Find the team with the next highest scoring tag
					let highestScore = -1;
					let bestTag = null;

					teamsWithRemainingTags.forEach(teamId => {
						const selectedTeamTags = selectedTags.filter(tag => tag.teamId === teamId);
						if (selectedTeamTags.length < teamTags[teamId].length) {
							const nextTag = teamTags[teamId][selectedTeamTags.length];
							if (nextTag.calculatedScore > highestScore) {
								highestScore = nextTag.calculatedScore;
								bestTag = nextTag;
							}
						}
					});

					if (bestTag) {
						const duration = bestTag.secondsAfter + bestTag.secondsBefore;
						if (duration <= remainingDuration) {
							selectedTags.push(bestTag);
						}
					}
				}
			}
		}
	}

	// Sort chronologically
	return selectedTags;
};

/**
 * Helper function to distribute tags between teams in a section
 * @param {Array} teamTags - Object mapping team IDs to their tags
 * @param {Number} quota - Number of tags to select
 * @returns {Array} Selected tags
 */
const distributeTagsBetweenTeams = (teamTags, quota) => {
	const teams = Object.keys(teamTags);
	if (teams.length === 0) return [];

	const selectedTags = [];
	let totalSelected = 0;

	if (teams.length === 1) {
		// Only one team, take top tags
		const teamId = teams[0];
		const tagsToTake = Math.min(quota, teamTags[teamId].length);
		selectedTags.push(...teamTags[teamId].slice(0, tagsToTake));
		totalSelected = tagsToTake;
	} else {
		// Multiple teams, distribute evenly
		const tagsPerTeam = Math.floor(quota / teams.length);
		const extraTag = quota % teams.length;

		// Find team with next highest score for extra tag
		let extraTagTeam = null;
		if (extraTag > 0) {
			let highestScore = -1;
			teams.forEach(teamId => {
				if (teamTags[teamId].length <= tagsPerTeam) return;
				const nextTagScore = teamTags[teamId][tagsPerTeam].calculatedScore;
				if (nextTagScore > highestScore) {
					highestScore = nextTagScore;
					extraTagTeam = teamId;
				}
			});
		}

		// Select tags for each team
		teams.forEach(teamId => {
			const teamQuota = teamId === extraTagTeam ? tagsPerTeam + 1 : tagsPerTeam;
			const tagsToTake = Math.min(teamQuota, teamTags[teamId].length);
			selectedTags.push(...teamTags[teamId].slice(0, tagsToTake));
			totalSelected += tagsToTake;
		});
	}

	return { selectedTags, totalSelected };
};

/**
 * Helper function to distribute duration between teams in a section
 * @param {Object} teamTags - Object mapping team IDs to their tags
 * @param {Number} targetDuration - Target duration for the section
 * @returns {Object} Selected tags and total duration
 */
const distributeDurationBetweenTeams = (teamTags, targetDuration) => {
	const teams = Object.keys(teamTags);
	if (teams.length === 0) return { selectedTags: [], totalDuration: 0 };

	const selectedTags = [];
	let totalDuration = 0;

	if (teams.length === 1) {
		// Only one team, take tags until reaching duration
		const teamId = teams[0];
		for (const tag of teamTags[teamId]) {
			const duration = tag.secondsAfter + tag.secondsBefore;
			if (totalDuration + duration > targetDuration) break;
			selectedTags.push(tag);
			totalDuration += duration;
		}
	} else {
		// Multiple teams, distribute duration evenly
		const durationPerTeam = targetDuration / teams.length;

		// First round: select tags for each team up to their duration quota
		teams.forEach(teamId => {
			let teamDuration = 0;
			for (const tag of teamTags[teamId]) {
				const duration = tag.secondsAfter + tag.secondsBefore;
				if (teamDuration + duration > durationPerTeam) break;
				selectedTags.push(tag);
				teamDuration += duration;
				totalDuration += duration;
			}
		});
	}

	return { selectedTags, totalDuration };
};

/**
 * Helper function to fill remaining quota with highest scored tags
 * @param {Array} allTags - All available tags
 * @param {Set} selectedIds - Set of already selected tag IDs
 * @param {Number} remainingQuota - Remaining number of tags to select
 * @returns {Array} Additional selected tags
 */
const fillWithHighestScoredTags = (allTags, selectedIds, remainingQuota) => {
	const remainingTags = allTags
		.filter(tag => !selectedIds.has(tag.id))
		.sort((a, b) => b.calculatedScore - a.calculatedScore);
	return remainingTags.slice(0, remainingQuota);
};

/**
 * Helper function to fill remaining duration with highest scored tags
 * @param {Array} allTags - All available tags
 * @param {Set} selectedIds - Set of already selected tag IDs
 * @param {Number} remainingDuration - Remaining duration to fill
 * @returns {Object} Additional selected tags and total duration
 */
const fillWithHighestScoredDuration = (allTags, selectedIds, remainingDuration) => {
	const selectedTags = [];
	let totalDuration = 0;

	const remainingTags = allTags
		.filter(tag => !selectedIds.has(tag.id))
		.sort((a, b) => b.calculatedScore - a.calculatedScore);

	for (const tag of remainingTags) {
		const duration = tag.secondsAfter + tag.secondsBefore;
		if (totalDuration + duration > remainingDuration) break;
		selectedTags.push(tag);
		totalDuration += duration;
	}

	return { selectedTags, totalDuration };
};

/**
 * Helper function to create sections from tags
 * @param {Array} tags - Tags to organize into sections
 * @param {Number} numberOfSections - Number of sections to create
 * @param {Function} getSectionIndex - Function to determine section index for a tag
 * @returns {Object} Sections and team tags
 */
const createSections = (tags, numberOfSections, getSectionIndex) => {
	const sections = Array(numberOfSections).fill().map(() => []);
	const sectionTeamTags = Array(numberOfSections).fill().map(() => ({}));

	tags.forEach(tag => {
		if (!tag.teamId) return;

		const sectionIndex = getSectionIndex(tag);
		if (sectionIndex === -1) return;

		// Add to general section array
		sections[sectionIndex].push(tag);

		// Add to team-specific collection within section
		if (!sectionTeamTags[sectionIndex][tag.teamId]) {
			sectionTeamTags[sectionIndex][tag.teamId] = [];
		}
		sectionTeamTags[sectionIndex][tag.teamId].push(tag);
	});

	// Sort tags by score within each team in each section
	sections.forEach((section, index) => {
		const teamTags = sectionTeamTags[index];
		Object.keys(teamTags).forEach(teamId => {
			teamTags[teamId].sort((a, b) => b.calculatedScore - a.calculatedScore);
		});
		section.sort((a, b) => b.calculatedScore - a.calculatedScore);
	});

	return { sections, sectionTeamTags };
};

/**
 * Select tags from time sections (for Condensed highlight type)
 * @param {Array} scoredTags
 * @param {Number} eventDuration
 * @param {String} limitType
 * @param {Number} limitValue
 * @param {Object} formulaConfig
 * @returns {Array}
 */
const selectTagsFromTimeSections = (scoredTags, eventDuration, limitType, limitValue, formulaConfig) => {
	// If not using game time or no period markers configured, use fallback with 10-minute blocks
	if (!formulaConfig.useGameTime || !formulaConfig.periodMarkers?.length) {
		return selectTagsFromTimeSectionsFallback(scoredTags, eventDuration, limitType, limitValue, formulaConfig);
	}

	const sections = [];
	const sectionTeamTags = [];

	// Process each period
	formulaConfig.periodMarkers.forEach((period) => {
		// Find period markers
		const periodStart = scoredTags.find(tag => tag.tag?.name === period.start);
		const periodEnd = scoredTags.find(tag => tag.tag?.name === period.end);

		if (!periodStart || !periodEnd) return;

		// Calculate period duration and section length
		const periodDuration = periodEnd.timestamp - periodStart.timestamp;
		const periodSectionLength = periodDuration / formulaConfig.blocksPerPeriod;

		// Create sections for this period
		const { sections: periodSections, sectionTeamTags: periodTeamTags } = createSections(
			scoredTags,
			formulaConfig.blocksPerPeriod,
			tag => {
				// Skip period markers
				if (tag.tag?.name === period.start || tag.tag?.name === period.end) {
					return -1;
				}

				// Only process tags within this period
				if (tag.timestamp >= periodStart.timestamp && tag.timestamp <= periodEnd.timestamp) {
					return Math.min(
						Math.floor((tag.timestamp - periodStart.timestamp) / periodSectionLength),
						formulaConfig.blocksPerPeriod - 1
					);
				}
				return -1;
			}
		);

		sections.push(...periodSections);
		sectionTeamTags.push(...periodTeamTags);
	});

	// If no sections were created, use fallback
	if (sections.length === 0) {
		return selectTagsFromTimeSectionsFallback(scoredTags, eventDuration, limitType, limitValue, formulaConfig);
	}

	const selectedTags = [];
	let totalSelected = 0;
	let totalDuration = 0;

	if (limitType === 'clips') {
		// Calculate tags per section
		const tagsPerSection = Math.floor(limitValue / sections.length);

		// Process each section
		for (let i = 0; i < sections.length; i++) {
			const { selectedTags: sectionTags, totalSelected: sectionSelected } = distributeTagsBetweenTeams(
				sectionTeamTags[i],
				tagsPerSection
			);
			selectedTags.push(...sectionTags);
			totalSelected += sectionSelected;
		}

		// Fill remaining quota if needed
		if (totalSelected < limitValue) {
			const selectedIds = new Set(selectedTags.map(tag => tag.id));
			const additionalTags = fillWithHighestScoredTags(
				scoredTags,
				selectedIds,
				limitValue - totalSelected
			);
			selectedTags.push(...additionalTags);
		}
	} else {
		// Duration-based selection
		const durationPerSection = limitValue / sections.length;

		// Process each section
		for (let i = 0; i < sections.length; i++) {
			const { selectedTags: sectionTags, totalDuration: sectionDuration } = distributeDurationBetweenTeams(
				sectionTeamTags[i],
				durationPerSection
			);
			selectedTags.push(...sectionTags);
			totalDuration += sectionDuration;
		}

		// Fill remaining duration if needed
		if (totalDuration < limitValue) {
			const selectedIds = new Set(selectedTags.map(tag => tag.id));
			const { selectedTags: additionalTags, totalDuration: additionalDuration } = fillWithHighestScoredDuration(
				scoredTags,
				selectedIds,
				limitValue - totalDuration
			);
			selectedTags.push(...additionalTags);
			totalDuration += additionalDuration;
		}
	}

	return selectedTags.sort((a, b) => a.timestamp - b.timestamp);
};

/**
 * Fallback implementation using fixed-length blocks (10 minutes by default)
 * @param {Array} scoredTags
 * @param {Number} eventDuration
 * @param {String} limitType
 * @param {Number} limitValue
 * @param {Object} formulaConfig
 * @returns {Array}
 */
const selectTagsFromTimeSectionsFallback = (scoredTags, eventDuration, limitType, limitValue, formulaConfig) => {
	// Use fallback block length (10 minutes) or calculate based on event duration
	const blockLength = formulaConfig.fallbackBlockLength || 10 * 60;
	const numberOfSections = Math.ceil(eventDuration / blockLength);

	// Create sections
	const { sections, sectionTeamTags } = createSections(
		scoredTags,
		numberOfSections,
		tag => Math.min(Math.floor(tag.timestamp / blockLength), numberOfSections - 1)
	);

	const selectedTags = [];
	let totalSelected = 0;
	let totalDuration = 0;

	if (limitType === 'clips') {
		// Calculate tags per section
		const tagsPerSection = Math.floor(limitValue / sections.length);

		// Process each section
		for (let i = 0; i < sections.length; i++) {
			const { selectedTags: sectionTags, totalSelected: sectionSelected } = distributeTagsBetweenTeams(
				sectionTeamTags[i],
				tagsPerSection
			);
			selectedTags.push(...sectionTags);
			totalSelected += sectionSelected;
		}

		// Fill remaining quota if needed
		if (totalSelected < limitValue) {
			const selectedIds = new Set(selectedTags.map(tag => tag.id));
			const additionalTags = fillWithHighestScoredTags(
				scoredTags,
				selectedIds,
				limitValue - totalSelected
			);
			selectedTags.push(...additionalTags);
		}
	} else {
		// Duration-based selection
		const durationPerSection = limitValue / sections.length;

		// Process each section
		for (let i = 0; i < sections.length; i++) {
			const { selectedTags: sectionTags, totalDuration: sectionDuration } = distributeDurationBetweenTeams(
				sectionTeamTags[i],
				durationPerSection
			);
			selectedTags.push(...sectionTags);
			totalDuration += sectionDuration;
		}

		// Fill remaining duration if needed
		if (totalDuration < limitValue) {
			const selectedIds = new Set(selectedTags.map(tag => tag.id));
			const { selectedTags: additionalTags, totalDuration: additionalDuration } = fillWithHighestScoredDuration(
				scoredTags,
				selectedIds,
				limitValue - totalDuration
			);
			selectedTags.push(...additionalTags);
			totalDuration += additionalDuration;
		}
	}

	return selectedTags.sort((a, b) => a.timestamp - b.timestamp);
};
